import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Trash2, HelpCircle, Info } from 'lucide-react';

const ConfirmContext = createContext(null);

const VARIANT_META = {
  destructive: {
    icon: Trash2,
    iconBg: 'bg-red-50 dark:bg-red-950/40',
    iconColor: 'text-red-600 dark:text-red-400',
    confirmClass: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
    defaultConfirm: 'Elimina',
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'bg-amber-50 dark:bg-amber-950/40',
    iconColor: 'text-amber-600 dark:text-amber-400',
    confirmClass: 'bg-amber-600 hover:bg-amber-700 text-white focus:ring-amber-500',
    defaultConfirm: 'Procedi',
  },
  default: {
    icon: HelpCircle,
    iconBg: 'bg-blue-50 dark:bg-blue-950/40',
    iconColor: 'text-blue-600 dark:text-blue-400',
    confirmClass: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500',
    defaultConfirm: 'Conferma',
  },
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    title: '',
    description: '',
    variant: 'default',
    confirmText: '',
    cancelText: 'Annulla',
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    // Support legacy call: confirm('string message')
    if (typeof opts === 'string') {
      opts = { description: opts };
    }
    const {
      title,
      description = '',
      variant = 'default',
      confirmText,
      cancelText = 'Annulla',
    } = opts;

    // Auto-infer destructive variant from keywords if not provided
    const inferredVariant = variant === 'default' && /elimin|rimuov|azzer|cancell|disdir/i.test(String(description) + String(title || ''))
      ? 'destructive'
      : variant;

    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        title: title || (inferredVariant === 'destructive' ? 'Conferma eliminazione' : 'Conferma azione'),
        description,
        variant: inferredVariant,
        confirmText: confirmText || VARIANT_META[inferredVariant].defaultConfirm,
        cancelText,
      });
    });
  }, []);

  // Lightweight toast-based confirm for soft/reversible actions
  const confirmToast = useCallback((opts = {}) => {
    if (typeof opts === 'string') opts = { description: opts };
    const { description = '', confirmText = 'Conferma', cancelText = 'Annulla' } = opts;
    return new Promise((resolve) => {
      const tId = toast(description, {
        duration: 10000,
        action: {
          label: confirmText,
          onClick: () => resolve(true),
        },
        cancel: {
          label: cancelText,
          onClick: () => resolve(false),
        },
        onDismiss: () => resolve(false),
        onAutoClose: () => resolve(false),
      });
      return tId;
    });
  }, []);

  const handleConfirm = () => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(true);
      resolverRef.current = null;
    }
  };

  const handleCancel = () => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
  };

  const meta = VARIANT_META[state.variant] || VARIANT_META.default;
  const Icon = meta.icon;

  return (
    <ConfirmContext.Provider value={{ confirm, confirmToast }}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) handleCancel(); }}>
        <AlertDialogContent
          data-testid="confirm-dialog"
          className="max-w-md rounded-2xl border-0 shadow-2xl"
        >
          <AlertDialogHeader>
            <div className="flex items-start gap-4">
              <div className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${meta.iconBg}`}>
                <Icon className={`w-5 h-5 ${meta.iconColor}`} />
              </div>
              <div className="flex-1 pt-0.5">
                <AlertDialogTitle className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  {state.title}
                </AlertDialogTitle>
                {state.description && (
                  <AlertDialogDescription className="mt-1.5 text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line">
                    {state.description}
                  </AlertDialogDescription>
                )}
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-2 gap-2">
            <AlertDialogCancel
              data-testid="confirm-dialog-cancel"
              onClick={handleCancel}
              className="rounded-xl border-slate-200 dark:border-slate-700"
            >
              {state.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-dialog-confirm"
              onClick={handleConfirm}
              className={`rounded-xl ${meta.confirmClass}`}
            >
              {state.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback to native (defensive): should never happen if Provider is mounted
    return {
      confirm: async (opts) => {
        const msg = typeof opts === 'string' ? opts : (opts?.description || opts?.title || 'Confermi?');
        // eslint-disable-next-line no-alert
        return window.confirm(msg);
      },
      confirmToast: async (opts) => {
        const msg = typeof opts === 'string' ? opts : (opts?.description || 'Confermi?');
        // eslint-disable-next-line no-alert
        return window.confirm(msg);
      },
    };
  }
  return ctx;
}

export default ConfirmContext;
